if (!customElements.get('voucher-form')) {
  customElements.define(
    'voucher-form',
    class VoucherForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.statusElement = this.querySelector('[data-voucher-status]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.apiEndpoint = this.dataset.apiEndpoint || '';
        this.variantId = this.dataset.variantId || '';
        this.source = this.dataset.source || 'product';
      }

      connectedCallback() {
        if (!this.form) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      async onSubmit(event) {
        event.preventDefault();
        this.setStatus('');

        const payload = this.getPayload();
        const validationMessage = this.validatePayload(payload);
        if (validationMessage) {
          this.setStatus(validationMessage, true);
          return;
        }

        if (!this.apiEndpoint) {
          this.setStatus('Voucher API endpoint is not configured.', true);
          return;
        }

        if (!this.variantId) {
          this.setStatus('Voucher variant ID is missing.', true);
          return;
        }

        this.setLoadingState(true);
        try {
          const voucherResponse = await this.createVoucher(payload);
          await this.addVoucherToCart(payload, voucherResponse);
          await this.refreshCartUI();
          this.setStatus('Voucher added to cart successfully.');
          this.form.reset();
        } catch (error) {
          this.setStatus(error.message || 'Unable to add voucher to cart.', true);
        } finally {
          this.setLoadingState(false);
        }
      }

      getPayload() {
        const formData = new FormData(this.form);
        return {
          name: (formData.get('name') || '').toString().trim(),
          email: (formData.get('email') || '').toString().trim(),
          phone: (formData.get('phone') || '').toString().trim(),
          color: (formData.get('color') || '').toString().trim(),
          amount: Number(formData.get('amount')),
        };
      }

      validatePayload(payload) {
        if (!payload.name) return 'Name is required.';
        if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) return 'Enter a valid email address.';
        if (!payload.phone || payload.phone.length < 7) return 'Enter a valid phone number.';
        if (!['Red', 'Blue', 'Yellow'].includes(payload.color)) return 'Select a valid voucher color.';
        if (!Number.isFinite(payload.amount) || payload.amount < 1 || payload.amount > 500) {
          return 'Voucher amount must be between $1 and $500.';
        }
        return '';
      }

      async createVoucher(payload) {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Voucher API validation failed.');
        }

        if (!data.voucher_id) {
          throw new Error('Voucher API response is missing voucher_id.');
        }

        return data;
      }

      async addVoucherToCart(payload, voucherResponse) {
        const properties = {
          Name: payload.name,
          Email: payload.email,
          Phone: payload.phone,
          'Voucher Color': payload.color,
          'Voucher Tier': this.getTierFromColor(payload.color),
          'Voucher Amount': Number(payload.amount).toFixed(2),
          'Voucher Price': this.getValidatedPrice(voucherResponse, payload.amount),
          'Voucher ID': voucherResponse.voucher_id,
        };

        if (voucherResponse.token) properties['Voucher Token'] = voucherResponse.token;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(this.variantId),
                quantity: 1,
                properties,
              },
            ],
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.description || 'Failed to add voucher item to cart.');
        }
      }

      getValidatedPrice(voucherResponse, fallbackAmount) {
        const rawPrice = voucherResponse.final_price ?? voucherResponse.validated_price ?? fallbackAmount;
        const parsed = Number(rawPrice);
        if (!Number.isFinite(parsed)) return Number(fallbackAmount).toFixed(2);
        return parsed.toFixed(2);
      }

      getTierFromColor(color) {
        const tiers = {
          Red: 'tier_red',
          Blue: 'tier_blue',
          Yellow: 'tier_yellow',
        };
        return tiers[color] || 'tier_unknown';
      }

      async refreshCartUI() {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'voucher-form',
            cartData,
            variantId: this.variantId,
          });
        }

        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.classList.remove('is-empty');
          drawer.open();
        } else if (this.source === 'drawer') {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      }

      setStatus(message, isError = false) {
        if (!this.statusElement) return;
        this.statusElement.textContent = message;
        this.statusElement.classList.toggle('voucher-form__status--error', Boolean(isError));
        this.statusElement.classList.toggle('voucher-form__status--success', Boolean(message) && !isError);
      }

      setLoadingState(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('disabled', isLoading);
        this.submitButton.classList.toggle('loading', isLoading);
      }
    }
  );
}
